import axiosInstance from '../utils/AxiosConfig';

export const getReplenishment = async (locationId, page = 1, pageSize = 10, searchTerm = '', replenishmentCategory = null, receptionStatus = 1) => {
  const params = new URLSearchParams({
    locationId: locationId.toString(),
    page: page.toString(),
    pageSize: pageSize.toString()
  });
  
  if (searchTerm && typeof searchTerm === 'string' && searchTerm.trim()) {
    params.append('search', searchTerm.trim());
  }
  
  if (replenishmentCategory !== null && replenishmentCategory !== undefined) {
    params.append('replenishmentCategory', replenishmentCategory.toString());
  }
  if (receptionStatus !== null && receptionStatus !== undefined) {
    params.append('receptionStatus', receptionStatus.toString());
  }
  
  const response = await axiosInstance.get(`/replenishments?${params}`);
  return response.data.data;
};

export const confirmReplenishmentReception = async (replenishmentId) => {
  const response = await axiosInstance.post(`/replenishments/${replenishmentId}/confirm-reception`);
  return response.data.data;
};

export const receivePendingReplenishment = async (replenishmentId, replenishmentData) => {
  try {
    const response = await axiosInstance.post(`/replenishments/${replenishmentId}/receive`, replenishmentData);
    return response.data.data;
  } catch (error) {
    console.error('Error receiving pending replenishment:', error);
    const priceError = normalizePriceDifferenceError(error);
    if (priceError) throw priceError;
    throw error;
  }
};

export const getReplenishmentById = async (replenishmentId, options = {}) => {
  const response = await axiosInstance.get(`/replenishments/${replenishmentId}`, {
    skipGlobalErrorRedirect: options.skipGlobalErrorRedirect === true
  });
  return response.data.data;
};

export const getDuplicateInvoiceReplenishments = async ({ locationId, supplierId, invoice }) => {
  const response = await axiosInstance.get('/replenishments/duplicate-invoice', {
    params: { locationId, supplierId, invoice },
    skipGlobalErrorRedirect: true,
  });
  return response.data?.data ?? response.data ?? [];
};

export const getLastReplenishmentPrices = async (items, currency) => {
  const response = await axiosInstance.post('/replenishments/last-prices', {
    items: items.map(item => ({
      reference_type: item.referenceType,
      reference_id: item.referenceId
    })),
    ...(currency ? { currency } : {})
  });
  return response.data.data?.prices || {};
};

export const addReplenishment = async (replenishmentData) => {
  try {    
    const response = await axiosInstance.post('/replenishments', replenishmentData);
    return response.data.data;
  } catch (error) {
    console.error('Error adding replenishment:', error);
    
    // Verificar si es un error de diferencia de precio
    if (error.response?.status === 400 && error.response?.data?.error_message) {
      const errorMessage = error.response.data.error_message;

      if (errorMessage.includes('duplicate_invoice_warning') || errorMessage.includes('Ya existe una reposición activa')) {
        const duplicateError = new Error('DUPLICATE_INVOICE_WARNING');
        duplicateError.originalError = error;
        duplicateError.skipSnackbar = true;
        throw duplicateError;
      }
      
      // Verificar si el mensaje contiene la advertencia de diferencia de precio
      if (errorMessage.includes('Algunos precios tienen una diferencia mayor al 10%') || 
          errorMessage.includes('Some prices have a difference greater than 10%')) {
        // El metadata viene en el formato: "mensaje: {"items":[...]}"
        let priceDifferences = [];
        
        // Intentar extraer del error_message que tiene el formato: "mensaje: {metadata}"
        if (errorMessage.includes('{')) {
          try {
            // Buscar el JSON en el mensaje (después de los dos puntos)
            const jsonMatch = errorMessage.match(/:\s*(\{.*\})/);
            if (jsonMatch) {
              const parsed = JSON.parse(jsonMatch[1]);
              if (parsed.items && Array.isArray(parsed.items)) {
                priceDifferences = parsed.items;
              }
            }
          } catch (e) {
            console.error('Error parsing error message:', e);
          }
        }
        
        // Si encontramos diferencias de precio, lanzar un error especial
        // Marcar el error para que no se muestre el snackbar automático
        if (priceDifferences.length > 0) {
          const priceError = new Error('PRICE_DIFFERENCE_WARNING');
          priceError.priceDifferences = priceDifferences;
          priceError.originalError = error;
          priceError.skipSnackbar = true; // Marcar para no mostrar snackbar
          throw priceError;
        }
      }
    }
    
    throw error;
  }
};

const normalizePriceDifferenceError = (error) => {
  if (error.response?.status === 400 && error.response?.data?.error_message) {
    const errorMessage = error.response.data.error_message;

    if (errorMessage.includes('Algunos precios tienen una diferencia mayor al 10%') ||
        errorMessage.includes('Some prices have a difference greater than 10%')) {
      let priceDifferences = [];

      if (errorMessage.includes('{')) {
        try {
          const jsonMatch = errorMessage.match(/:\s*(\{.*\})/);
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[1]);
            if (parsed.items && Array.isArray(parsed.items)) {
              priceDifferences = parsed.items;
            }
          }
        } catch (e) {
          console.error('Error parsing error message:', e);
        }
      }

      if (priceDifferences.length > 0) {
        const priceError = new Error('PRICE_DIFFERENCE_WARNING');
        priceError.priceDifferences = priceDifferences;
        priceError.originalError = error;
        priceError.skipSnackbar = true;
        return priceError;
      }
    }
  }

  return null;
};

export const updateReplenishment = async (replenishmentId, replenishmentData) => {
  try {
    const response = await axiosInstance.put(`/replenishments/${replenishmentId}`, replenishmentData);
    return response.data.data;
  } catch (error) {
    console.error('Error updating replenishment:', error);
    const priceError = normalizePriceDifferenceError(error);
    if (priceError) throw priceError;
    throw error;
  }
};

export const updateReplenishmentStatus = async (replenishmentId, status) => {
  try {
    const response = await axiosInstance.put(`/replenishments/${replenishmentId}/status`, { 
      status: status
     });
    return response.data.data;
  } catch (error) {
    console.error('Error updating replenishment status:', error);
    throw new Error(error.response?.data?.error_message || 'Failed to update replenishment status');
  }
};

export const updateReplenishmentPayment = async (replenishmentId, paymentData) => {
  try {
    const response = await axiosInstance.put(`/replenishments/${replenishmentId}/payment`, paymentData);
    return response.data.data;
  } catch (error) {
    console.error('Error updating replenishment payment:', error);
    throw new Error(error.response?.data?.error_message || 'Failed to update replenishment payment');
  }
};

export const uploadInvoiceFile = async (file, replenishmentId) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosInstance.post(`/replenishments/${replenishmentId}/uploadInvoice`, formData, {
      headers: {
          'Content-Type': 'multipart/form-data'
      }
  });
  return response.data.data;
};

export const deleteInvoiceFile = async (replenishmentId) => {
  const response = await axiosInstance.delete(`/replenishments/${replenishmentId}/invoice`);
  return response.data.data;
};

export const cancelReplenishment = async (replenishmentId) => {
  const response = await axiosInstance.delete(`/replenishments/${replenishmentId}`);
  return response.data.data;
};
